/**
 * drawer.js — <theme-overlay>
 *
 * One element behind every overlay in the theme: the mobile menu, the search
 * sheet and the cart drawer. Writing the keyboard and focus behaviour once is
 * the only realistic way to make it identical in all three.
 *
 * Markup contract:
 *   <button data-overlay-open="cart-drawer" aria-expanded="false">
 *   <theme-overlay id="cart-drawer" class="overlay overlay--side" hidden>
 *     <div class="overlay__backdrop" data-overlay-close></div>
 *     <div class="overlay__panel" role="dialog" aria-modal="true" aria-label="…">
 *
 * Behaviour: Escape closes, backdrop closes, focus is trapped while open and
 * returned to the trigger on close, background scroll is locked without losing
 * scroll position, and `hidden` is removed a frame before the open class is
 * added so the entry transition actually runs.
 */

import { trapFocus, lockScroll, unlockScroll } from './theme.js';

class ThemeOverlay extends HTMLElement {
  constructor() {
    super();
    this.releaseFocus = null;
    this.opener = null;
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.panel = this.querySelector('.overlay__panel');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-overlay-close]')) {
        event.preventDefault();
        this.close();
      }
    });
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open(opener) {
    if (this.isOpen) return;

    this.opener = opener || null;
    this.hidden = false;

    // Force a synchronous style flush so the closed transform is committed
    // before `is-open` changes it — otherwise the panel appears already open
    // with no transition. Deliberately not requestAnimationFrame: rAF is
    // throttled or suspended in background tabs and under low-power modes, and
    // an overlay that fails to add `is-open` is invisible *while trapping
    // focus inside it*. A forced reflow always runs.
    void this.offsetWidth;
    this.classList.add('is-open');

    lockScroll();
    document.addEventListener('keydown', this.onKeydown);

    const initial = this.querySelector('[data-overlay-initial-focus]');
    this.releaseFocus = trapFocus(this.panel || this, initial || undefined);

    if (this.opener) this.opener.setAttribute('aria-expanded', 'true');
    this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKeydown);
    unlockScroll();

    if (this.releaseFocus) {
      this.releaseFocus({ restoreFocus: true });
      this.releaseFocus = null;
    }

    if (this.opener) this.opener.setAttribute('aria-expanded', 'false');

    // Stay in the accessibility tree until the exit transition finishes, so
    // the panel does not vanish mid-animation.
    const done = () => { this.hidden = true; };
    const duration = this.motionDuration();

    if (duration === 0) {
      done();
    } else {
      window.setTimeout(done, duration);
    }

    this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
  }

  toggle(opener) {
    this.isOpen ? this.close() : this.open(opener);
  }

  motionDuration() {
    if (!this.panel) return 0;
    const value = getComputedStyle(this.panel).transitionDuration.split(',')[0];
    return value.includes('ms') ? parseFloat(value) : parseFloat(value) * 1000;
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }
}

if (!customElements.get('theme-overlay')) {
  customElements.define('theme-overlay', ThemeOverlay);
}

/**
 * Delegated triggers. A single listener means overlays injected later — by the
 * theme editor, or by a section re-render — work without re-binding.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-overlay-open]');
  if (!trigger) return;

  const overlay = document.getElementById(trigger.getAttribute('data-overlay-open'));
  if (!overlay || typeof overlay.toggle !== 'function') return;

  event.preventDefault();
  overlay.toggle(trigger);
});

export default ThemeOverlay;
